import { useCallback, useMemo } from 'react';

import { useUser } from '@clerk/nextjs';
import { useSessionStorage } from '@uidotdev/usehooks';

import { useAppParams } from '@/hooks/use-app-params';

import { Analytics } from './index';

export function useAnalytics() {
  const { user, isLoaded } = useUser();
  const appParams = useAppParams();
  const [_, setSourceEvent] = useSessionStorage('__source_event', {});

  const userData = useMemo(() => {
    if (!(isLoaded && user)) {
      return {};
    }

    return {
      user_id: user.id,
      external_id: user.externalId,
      email: user.emailAddresses?.[0]?.emailAddress,
      first_name: user.firstName,
      last_name: user.lastName,
    };
  }, [isLoaded, user]);

  const appParamsData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          externship_id: appParams.externshipId,
          externship_module_id: appParams.moduleId,
          externship_step_id: appParams.stepId,
          externship_substep_id: appParams.substepId,
        }).filter(([_, value]) => value),
      ),
    [appParams.substepId, appParams.stepId, appParams.moduleId, appParams.externshipId],
  );

  const track = useCallback(
    (event: string, properties?: Record<string, unknown>, callback?: () => void) => {
      Analytics.track(
        event,
        {
          ...appParamsData,
          ...userData,
          ...properties,
          timestamp: new Date().toISOString(),
        },
        undefined,
        callback,
      );
    },
    [userData, appParamsData],
  );

  const page = useCallback(
    (page: string, properties?: Record<string, unknown>) => {
      Analytics.page(page, {
        ...appParamsData,
        ...properties,
        ...userData,
        timestamp: new Date().toISOString(),
      });
    },
    [userData, appParamsData],
  );

  const trackSourceEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      setSourceEvent({
        source_event: event,
        ...properties,
      });
    },
    [setSourceEvent],
  );

  const identify = useCallback(
    (properties?: Record<string, unknown>) => {
      Analytics.identify(userData.user_id, {
        ...appParamsData,
        ...userData,
        ...properties,
      });
    },
    [userData, appParamsData],
  );

  return { track, page, identify, trackSourceEvent };
}
